import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import TestingSection from '@features/costing-agent/Components/TestingSection';

import { checkAnyUserType } from '@utils/Util';

import { toastError, toastSuccess } from '@/components/Toast';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import LoaderComponent from '@/components/LoaderComponent';

import VersionSidebar from './Components/VersionSidebar';
import Header from './Components/Header';
import FabricTable from './Components/FabricTable';
import PrintTable from './Components/PrintTable';
import TrimsSection from './Components/TrimsSection';
import PackagingSection from './Components/PackagingSection';
import CMSection from './Components/CMSection';
import CommercialSection from './Components/CommercialSection';
import CostingSummary from './Components/CostingSummary';
import ActionButtons from './Components/ActionButtons';
import ProductWisePriceRange from './Components/ProductWisePriceRange';
import {
	useGetCostingByProductIdQuery,
	useGetHistoryByProductIdQuery,
	useGetPriceRangeByProductIdQuery,
	useSubmitCostingMutation,
	useGetCostingByHistoryIdQuery,
	useLazyLikeUnlikeCostingQuery,
	useHandleRecostMutation,
	useGetProductInfoByProductIdQuery
} from './api/costingAgentApi';
import { LoadingState } from './Components/LoadingState';
import EmbroideryTable from './Components/EmbroideryTable';
import WashTable from './Components/WashTable';
import { validateRequiredFields as validateRequiredFieldsUtil } from './utils';
import FabricSustainabilityTable from './Components/FabricSustainabilityTable';

const CostingAgent = ({ setLoader }) => {
	const { id } = useParams();
	const [showVersionSidebar, setShowVersionSidebar] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [hasFileChanges, setHasFileChanges] = useState(false);
	const [formData, setFormData] = useState(null);
	const [selectedHistoryId, setSelectedHistoryId] = useState(null);
	const location = useLocation();
	const navigate = useNavigate();
	const hasProcessedInitialLoad = useRef(false);
	const [shouldFetch, setShouldFetch] = useState(false);
	const [isEmptyHistory, setIsEmptyHistory] = useState(false);
	const [changedSections, setChangedSections] = useState(new Set());
	const [hasSubmittedCosting, setHasSubmittedCosting] = useState(false);
	const [recostResponse, setRecostResponse] = useState(null);
	const [validationErrors, setValidationErrors] = useState({});
	const [costingBrandInfo, setCostingBrandInfo] = useState({
		brandId: null,
		brandName: null,
		singleQty: null,
		initialCostingId: null,
		setProductId: null,
		isBuyerRequest: true
	});
	const [selectedFileList, setSelectedFileList] = useState({});
	const dispatch = useDispatch();
	const editableRoles = () => {
		return checkAnyUserType(['ADMIN', 'PRODUCT_DEVELOPMENT', 'COSTING_MANAGER', 'ACCOUNT_MANAGER']);
	};

	const isEditable = editableRoles();
	const {
		data: costingData,
		refetch: refetchAiCosting,
		isLoading,
		isFetching: isCostingFetching,
		error: costingError
	} = useGetCostingByProductIdQuery(
		{
			productId: id,
			brandName: costingBrandInfo?.brandName?.trim(),
			singleQty: costingBrandInfo?.singleQty || 1000,
			isBuyerRequest: costingBrandInfo?.isBuyerRequest || false,
			initialCostingId: costingBrandInfo?.initialCostingId || null
		},
		{ skip: !shouldFetch }
	);

	const {
		data: historyData,
		refetch: refetchHistory,
		isLoading: historyLoading
	} = useGetHistoryByProductIdQuery({
		productId: id,
		brandId: new URLSearchParams(location.search).get('brandId'),
		initialCostingId: new URLSearchParams(location.search).get('initialCostingId')
	});
	const [submitCosting] = useSubmitCostingMutation();
	const [handleRecost] = useHandleRecostMutation();
	const [likeUnlikeCosting, { isLoading: isLikeUnlikeLoading }] = useLazyLikeUnlikeCostingQuery();
	const {
		data: priceRangeData,
		isLoading: isPriceRangeLoading,
		error: priceRangeError
	} = useGetPriceRangeByProductIdQuery(id);
	const { data: productInfoData } = useGetProductInfoByProductIdQuery(id);

	const hasFileChange = () => {
		setHasChanges(true);
		setHasFileChanges(true);
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const brandId = params.get('brandId') || '1';
		const brandName = params.get('brandName') || 'Zalando';
		const singleQty = params.get('singleQty') || 1000;
		const isBuyerRequest = params.get('isBuyerRequest') || false;
		const initialCostingId = params.get('initialCostingId') || null;
		const setProductId = params.get('setProductId') || null;
		setCostingBrandInfo((prev) => ({
			...prev,
			brandId,
			brandName,
			singleQty,
			isBuyerRequest,
			initialCostingId,
			setProductId
		}));
	}, [location.search]);

	useEffect(() => {}, [priceRangeData]);

	useEffect(() => {
		if (priceRangeError) {
			toastError('Error fetching price range data');
		}
	}, [priceRangeError]);

	const getHistoryId = () => {
		if (!historyData?.payload || historyData.payload.length === 0) {
			return null;
		}
		const approvedItem = historyData.payload.find((item) => item.approved === true);
		return approvedItem ? approvedItem.id : historyData.payload[0]?.id;
	};
	const historyId = selectedHistoryId || getHistoryId();
	const {
		data: historyCostingData,
		refetch: refetchHistoryCosting,
		isLoading: isHistoryCostingLoading
	} = useGetCostingByHistoryIdQuery({ productId: id, historyId }, { skip: !historyId });

	useEffect(() => {
		if (historyData?.payload && historyData.payload.length === 0) {
			setIsEmptyHistory(true);
		} else {
			setIsEmptyHistory(false);
		}
	}, [historyData]);

	useEffect(() => {
		if (historyCostingData?.payload) {
			setHasSubmittedCosting(historyCostingData?.payload?.isSubmitted || false);
		}
	}, [historyCostingData, selectedHistoryId]);

	useEffect(() => {
		if (historyData?.payload && historyData.payload.length > 0 && !selectedHistoryId) {
			const approvedItem = historyData.payload.find((item) => item.approved === true);
			const initialHistoryId = approvedItem ? approvedItem.id : historyData.payload[0]?.id;
			setSelectedHistoryId(initialHistoryId);
		}
	}, [historyData, selectedHistoryId]);

	useEffect(() => {
		setLoader(false);
	}, [id, setLoader]);

	useEffect(() => {
		if (costingError) {
			toastError(costingError?.data?.message || 'Error fetching costing data');
		}
	}, [costingError]);

	//after fetching costing data, set shouldFetch to false
	useEffect(() => {
		if (shouldFetch && !isCostingFetching) {
			setShouldFetch(false);
		}
	}, [shouldFetch, isCostingFetching]);

	useEffect(() => {
		if (!hasProcessedInitialLoad.current && isEmptyHistory) {
			const params = new URLSearchParams(location.search);
			const isAiCostingOnce = params.get('isAiCosting') === 'true';
			if (isAiCostingOnce) {
				setShouldFetch(true);
				hasProcessedInitialLoad.current = true;
				params.delete('isAiCosting');
				navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
			} else {
				hasProcessedInitialLoad.current = true;
			}
		}
	}, [location.search, location.pathname, navigate, isEmptyHistory]);

	useEffect(() => {
		if (costingData?.payload) {
			const payload = costingData?.payload;
			setFormData({ ...payload.costingDetails });
			setSelectedHistoryId(payload?.id);
			setHasChanges(false);
			setChangedSections(new Set());
			setRecostResponse(null);
			refetchHistory();
		}
	}, [costingData, refetchHistory]);

	useEffect(() => {
		if (historyCostingData?.payload) {
			setFormData({ ...historyCostingData.payload.costingDetails });
			setHasChanges(false);
			setChangedSections(new Set());
		}
	}, [historyCostingData]);

	const sectionToChangeType = {
		supplierChange: 'SUPPLIER_CHANGE',
		fabricWisePriceBreakdownList: 'FABRIC_CHANGE',
		fiberRequirementList: 'SUSTAINABILITY_CHANGE',
		printPriceList: 'PRINT_CHANGE',
		embroideryPriceList: 'EMBROIDERY_CHANGE',
		washPriceBreakdown: 'WASH_CHANGE',
		trimsDetailsList: 'TRIMS_CHANGE',
		trimsAdditionalCharge: 'TRIMS_CHANGE',
		packagingPriceCostBreakdown: 'PACKAGING_CHANGE',
		cmCost: 'CM_CHANGE',
		commercialCostPercentage: 'COMMERCIAL_CHANGE',
		perStyleTestingCost: 'TESTING_COST_CHANGE'
	};

	const handleDataChange = useCallback((section) => {
		setHasChanges(true);
		if (section && sectionToChangeType[section]) {
			setChangedSections((prev) => new Set([...prev, sectionToChangeType[section]]));
		}
	}, []);

	const validateRequiredFields = () => {
		const errors = validateRequiredFieldsUtil(formData);
		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const processSelectedFiles = (files) => {
		return (
			files?.map((item) => ({
				docId: item.id,
				docName: item.label,
				docUrl: item.clickAbleLink,
				sheetName: item?.sheetName || null
			})) || []
		);
	};

	const reCostIndividually = async (changeType, data) => {
		dispatch(openLoader());
		try {
			let requestBody = {
				techpackCostingDataDTO: { ...formData },
				changeTypeList: [changeType]
			};
			if (changeType === 'CAD_WASTAGE_PERCENTAGE_CHANGE') {
				requestBody.techpackCostingDataDTO.cadWiseConsumptionList = data;
			} else if (changeType === 'MEASUREMENT_CHANGE') {
				requestBody.techpackCostingDataDTO.fabricWiseMeasurements = data;
			} else if (changeType === 'WASH_CHANGE') {
				requestBody.techpackCostingDataDTO.washPriceBreakdown = data;
			} else if (changeType === 'EMBROIDERY_CHANGE') {
				requestBody.techpackCostingDataDTO.embroideryPriceList = data;
			} else if (changeType === 'PRINT_CHANGE') {
				requestBody.techpackCostingDataDTO.printPriceList = data;
			} else if (changeType === 'FABRIC_CHANGE') {
				requestBody.techpackCostingDataDTO.fabricWisePriceBreakdownList = data;
			} else if (changeType === 'FILE_CHANGE') {
				let patternIds = selectedFileList?.['Pattern']
					? Array.from(selectedFileList?.['Pattern'])
					: [];
				let techpackIds = selectedFileList?.['Techpack']
					? Array.from(selectedFileList?.['Techpack'])
					: [];
				let otherIds = selectedFileList?.['Others'] ? Array.from(selectedFileList?.['Others']) : [];
				let measurements = selectedFileList?.['Measurement']
					? Array.from(selectedFileList?.['Measurement'])
					: [];
				if (
					techpackIds?.length === 0 &&
					costingBrandInfo?.isBuyerRequest &&
					!isShowNitexTechpackLabel
				) {
					return toastError('Please select at least one techpack file to recost');
				}

				if (
					(!historyId || !formData || changedSections.size === 0) &&
					patternIds.length === 0 &&
					techpackIds.length === 0 &&
					measurements.length === 0 &&
					otherIds.length === 0
				)
					return;

				if (patternIds.length > 0 || techpackIds.length > 0 || measurements.length > 0) {
					requestBody = {
						...requestBody,
						patternFiles: processSelectedFiles(patternIds),
						techpackFiles: processSelectedFiles(techpackIds),
						measurementFiles: processSelectedFiles(measurements),
						otherFiles: processSelectedFiles(otherIds)
					};
				}
			}

			const response = await handleRecost({
				productId: id,
				historyId,
				brandName: costingBrandInfo?.brandName?.trim(),
				singleQty: costingBrandInfo?.singleQty || 1000,
				isBuyerRequest: costingBrandInfo?.isBuyerRequest,
				requestBody: requestBody,
				initialCostingId: costingBrandInfo?.initialCostingId
			}).unwrap();

			if (response?.payload) {
				setFormData({ ...response.payload.costingDetails });
				setRecostResponse(response.payload);
				setSelectedHistoryId(response.payload.id);
				setValidationErrors({});
				refetchHistory();
				toastSuccess('Re-costed Successful');
			}
		} catch (error) {
			toastError(error?.data?.message || 'Error recalculating Measurement');
		} finally {
			dispatch(closeLoader());
		}
	};

	const onReCost = async () => {
		dispatch(openLoader());
		const changedList = Array.from(changedSections);
		if (changedList.length === 0 && !hasFileChanges) {
			dispatch(closeLoader());
			return;
		}
		if (hasFileChanges) {
			changedList.push('FILE_CHANGE');
		}
		try {
			let requestBody = {
				techpackCostingDataDTO: { ...formData },
				changeTypeList: changedList
			};

			if (hasFileChanges) {
				let patternIds = selectedFileList?.['Pattern']
					? Array.from(selectedFileList?.['Pattern'])
					: [];
				let techpackIds = selectedFileList?.['Techpack']
					? Array.from(selectedFileList?.['Techpack'])
					: [];
				let otherIds = selectedFileList?.['Others'] ? Array.from(selectedFileList?.['Others']) : [];
				let measurements = selectedFileList?.['Measurement']
					? Array.from(selectedFileList?.['Measurement'])
					: [];
				if (
					techpackIds?.length === 0 &&
					costingBrandInfo?.isBuyerRequest &&
					!isShowNitexTechpackLabel
				) {
					return toastError('Please select at least one techpack file to recost');
				}

				if (
					(!historyId || !formData || changedSections.size === 0) &&
					patternIds.length === 0 &&
					techpackIds.length === 0 &&
					measurements.length === 0 &&
					otherIds.length === 0
				)
					return;

				if (patternIds.length > 0 || techpackIds.length > 0 || measurements.length > 0) {
					requestBody = {
						...requestBody,
						patternFiles: processSelectedFiles(patternIds),
						techpackFiles: processSelectedFiles(techpackIds),
						measurementFiles: processSelectedFiles(measurements),
						otherFiles: processSelectedFiles(otherIds)
					};
				}
			}

			const response = await handleRecost({
				productId: id,
				historyId,
				brandName: costingBrandInfo?.brandName?.trim(),
				singleQty: costingBrandInfo?.singleQty || 1000,
				isBuyerRequest: costingBrandInfo?.isBuyerRequest,
				requestBody: requestBody,
				initialCostingId: costingBrandInfo?.initialCostingId
			}).unwrap();

			if (response?.payload) {
				setFormData({ ...response.payload.costingDetails });
				setRecostResponse(response.payload);
				setSelectedHistoryId(response.payload.id);
				setValidationErrors({});
				refetchHistory();
				setHasFileChanges(false);
				toastSuccess('Re-costed Successful');
			}
		} catch (error) {
			toastError(error?.data?.message || 'Error recalculating Measurement');
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleSubmit = async () => {
		if (!historyId) return;

		if (!validateRequiredFields()) {
			toastError('Please fill in all required fields');
			return;
		}

		dispatch(openLoader());
		try {
			await submitCosting({
				productId: id,
				historyId,
				requestBody: formData,
				initialCostingId: costingBrandInfo?.initialCostingId,
				setProductId: costingBrandInfo?.setProductId
			}).unwrap();
			setHasSubmittedCosting(true);
			setValidationErrors({});
			refetchHistoryCosting();
			toastSuccess('Costing submitted successfully');
		} catch (error) {
			toastError(error.data.message);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleLikeUnlike = async (isLiked) => {
		if (!historyId) return;
		try {
			await likeUnlikeCosting({ productId: id, historyId, isLiked });
			refetchHistory();
		} catch (error) {
			toastError(error?.data?.message || 'Failed to update like status');
		}
	};

	const getLikeStatus = () => {
		if (!historyData?.payload || !historyId) return null;
		const selectedItem = historyData.payload.find((item) => item.id === historyId);
		return selectedItem?.isLiked ?? null;
	};

	const isLiked = getLikeStatus();

	const updateFormData = useCallback(
		(section, data) => {
			setFormData((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					[section]: data
				};
			});
			handleDataChange(section);
		},
		[handleDataChange]
	);

	const onGenerateAiCosting = () => {
		if (shouldFetch || costingData) {
			refetchAiCosting();
		}
		setShouldFetch(true);
	};

	const isShowNitexTechpackLabel =
		recostResponse?.payload?.isNitexTechpack === true ||
		costingData?.payload?.isNitexTechpack === true ||
		historyCostingData?.payload?.isNitexTechpack === true;

	const usedDocuments =
		recostResponse?.payload?.usedDocumentIds ||
		costingData?.payload?.usedDocumentIds ||
		historyCostingData?.payload?.usedDocumentIds;

	const responsePayload =
		recostResponse?.costingDetails ||
		costingData?.payload.costingDetails ||
		historyCostingData?.payload.costingDetails;
	const payload = formData || responsePayload?.costingDetails || {};

	if (isLoading || isCostingFetching) {
		return (
			<div className="bg-white costing-agent-container d-flex justify-content-center align-items-center">
				<LoadingState isLoading={true} />
			</div>
		);
	}

	if (isEmptyHistory) {
		return (
			<div className="bg-white costing-agent-container d-flex justify-content-center align-items-center">
				<div className="text-center">
					<h3 className="regular-16">No costing has been done yet</h3>
					<p className="regular-14 gray_dark_02 mb-3">
						Get started by using AI to generate an instant <br /> cost breakdown for this design.
					</p>
					<button
						className="button primary size32"
						onClick={onGenerateAiCosting}
						disabled={!isEditable}
					>
						<span>Generate AI Costing</span>
					</button>
				</div>
			</div>
		);
	}

	const openTechpack = () => {
		const url = window.location.href;
		const separator = url.includes('?') ? '&' : '?';
		window.open(`${url}${separator}isOpenTechpackViewModal=true`, '_blank');
	};

	return (
		<LoaderComponent loading={isHistoryCostingLoading || historyLoading}>
			<div className="costing-agent-container bg-white">
				<Header
					onHistoryClick={() => setShowVersionSidebar(true)}
					costingBrandInfo={costingBrandInfo}
					baseQty={payload?.quantity}
					selectedFileList={selectedFileList}
					setSelectedFileList={setSelectedFileList}
					usedDocumentIds={usedDocuments}
					isShowNitexTechpackLabel={isShowNitexTechpackLabel}
					isFileChanged={hasFileChanges}
					onEyeClick={openTechpack}
					reCostChange={reCostIndividually}
					hasFileChange={hasFileChange}
				/>

				<div className="costing-table-container relative">
					<div className="costing-table-and-summary d-flex flex-wrap gap-3">
						<div className="all-costing-table mb-4">
							{payload?.fiberRequirementList &&
								Array.isArray(payload?.fiberRequirementList) &&
								payload?.fiberRequirementList.length > 0 && (
									<FabricSustainabilityTable
										data={payload?.fiberRequirementList}
										onChange={(data) => updateFormData('fiberRequirementList', data)}
									/>
								)}

							<FabricTable
								data={payload?.fabricWisePriceBreakdownList}
								cadConsumptions={payload?.cadWiseConsumptionList}
								fabricWiseMeasurements={payload?.fabricWiseMeasurements}
								totalCost={payload?.fabricCost || 0}
								measurementSizeRange={payload?.measurementSizeRange || ''}
								selectedMeasurementSize={payload?.selectedMeasurementSize || ''}
								onChange={(data) => updateFormData('fabricWisePriceBreakdownList', data)}
								onMeasurementsChange={(data) =>
									updateFormData('fabricWiseMeasurements', data)
								}
								onConsumptionTypeChange={(data) =>
									updateFormData('selectedConsumptionType', data)
								}
								isEditable={isEditable}
								validationErrors={validationErrors}
								onReCost={reCostIndividually}
								selectedConsumptionType={payload?.selectedConsumptionType || ''}
							/>
							<PrintTable
								data={payload?.printPriceList}
								totalCost={payload?.printCost || 0}
								onChange={(data) => updateFormData('printPriceList', data)}
								isEditable={isEditable}
								validationErrors={validationErrors}
							/>
							<EmbroideryTable
								data={payload?.embroideryPriceList}
								totalCost={payload?.embroideryCost || 0}
								onChange={(data) => updateFormData('embroideryPriceList', data)}
								isEditable={isEditable}
								validationErrors={validationErrors}
							/>
							<WashTable
								data={payload?.washPriceBreakdown}
								totalCost={payload?.washCost || 0}
								onChange={(data) => updateFormData('washPriceBreakdown', data)}
								isEditable={isEditable}
								validationErrors={validationErrors}
							/>
							<TrimsSection
								data={payload?.trimsDetailsList}
								totalCost={payload?.trimsCost || 0}
								onChange={(data) => updateFormData('trimsDetailsList', data)}
								trimsAdditionalCharge={payload?.trimsAdditionalCharge}
								onTrimsAdditionalChargeChange={(value) =>
									updateFormData('trimsAdditionalCharge', value)
								}
								isEditable={isEditable}
							/>
							<PackagingSection
								data={payload?.packagingPriceCostBreakdown}
								totalCost={payload?.packagingCost || 0}
								onChange={(data) => updateFormData('packagingPriceCostBreakdown', data)}
								isEditable={isEditable}
							/>
							<CMSection
								value={payload?.cmCost}
								totalCost={responsePayload?.cmCost || payload?.cmCost || 0}
								onChange={(value) => updateFormData('cmCost', value)}
								isEditable={isEditable}
							/>
							<TestingSection
								perStyleTestingCost={payload?.perStyleTestingCost}
								totalCost={payload?.perPisTestingCost || 0}
								onChange={(value) => updateFormData('perStyleTestingCost', value)}
								isEditable={isEditable}
							/>
							<CommercialSection
								value={payload?.commercialCostPercentage}
								totalCost={payload?.commercialCost || 0}
								onChange={(value) => updateFormData('commercialCostPercentage', value)}
								isEditable={isEditable}
							/>
						</div>
						<div className="costing-summary-container">
							<ActionButtons
								hasChanges={hasChanges}
								onSubmit={handleSubmit}
								isEditable={isEditable}
								hasSubmittedCosting={hasSubmittedCosting}
								onReCost={onReCost}
							/>
							<CostingSummary
								summary={{
									fabricConsumption: payload?.estimatedConsumption || 0,
									fabricCost: payload?.fabricCost || 0,
									printCost: payload?.printCost || 0,
									embroideryCost: payload?.embroideryCost || 0,
									trimsCost: payload?.trimsCost || 0,
									cmCost: responsePayload?.cmCost || payload?.cmCost || 0,
									washCost: payload?.washCost || 0,
									packaging: payload?.packagingCost || 0,
									commercial: payload?.commercialCost || 0,
									perPisTestingCost: payload?.perPisTestingCost || 0
								}}
								historyId={historyId}
								isLiked={isLiked}
								onLike={() => handleLikeUnlike(true)}
								onDislike={() => handleLikeUnlike(false)}
								isLoading={isLikeUnlikeLoading}
								totalFobPerUnit={payload?.predictedCost || 0}
								baseQty={payload?.quantity}
								costingBrandInfo={costingBrandInfo}
								quantityWisePrices={payload?.qtyWisePriceList || []}
							/>
							<ProductWisePriceRange
								priceRangeData={priceRangeData?.payload || {}}
								isPriceRangeLoading={isPriceRangeLoading}
								predictedCost={payload?.predictedCost || 0}
								category={productInfoData?.subCategoryResponse?.name}
							/>
						</div>
					</div>
				</div>
				{showVersionSidebar && (
					<VersionSidebar
						productId={id}
						selectedHistoryId={selectedHistoryId}
						onHistorySelect={setSelectedHistoryId}
						onClose={() => setShowVersionSidebar(false)}
						brandId={costingBrandInfo?.isBuyerRequest ? costingBrandInfo?.brandId : null}
						initialCostingId={
							costingBrandInfo?.isBuyerRequest ? costingBrandInfo?.initialCostingId : null
						}
					/>
				)}
			</div>
		</LoaderComponent>
	);
};

export default CostingAgent;
